import { Field, FieldProps, FormikProps } from 'formik';
import * as React from 'react';
import { OperationVariables, QueryResult } from '@apollo/client';
import {
  FieldRow,
  Flex,
  StyledFormGroup,
  StyledFormikDatePicker,
  StyledFormSelectFormik,
  WrappedQuery,
} from '../../../../components/shared';
import {
  ALL_EDUCATOR_LEVELS,
  ALL_GRADES,
  ALL_HIGHEST_QUALIFICATIONS,
  ALL_NATURE_OF_EMPLOYMENTS,
  FILTERED_PERSONNEL_POSITIONS,
  FILTERED_REASON_FOR_TERMINATIONS,
  ALL_RENUMERATION_SOURCES,
  FILTERED_SUBJECTS,
  ALL_ICT_SKILLS,
} from '../../../../graphql';
import { formatSelectValue } from '../../../../utils';
import { Option, OptionNumber, PeopleProfileInstitutionFormType } from '../../../../utils/types';
import { InstitutionsCard } from './institutionsCard';
import { PersonDetailsFormType } from '../forms/PersonDetailsForm';

type FormType = PersonDetailsFormType & {
  typeId: Option | undefined;
  portal: string;

  dateExited: Date | null;
  dateStarted: Date | null;
  educatorLevelId: Option | undefined;
  emailAddress: string;
  emisNumber: number | undefined;
  districtId: Option | undefined;
  gradeIds: Option[];
  highestQualificationId: Option | undefined;
  ictSkillId: Option | undefined;
  leadEducator: boolean;
  multiGrade: boolean;
  natureOfEmploymentId: Option | undefined;
  personnelPositionId: Option | undefined;
  personnelPositionOther: string;
  postLevel: OptionNumber | undefined;
  renumerationSourceId: Option | undefined;
  subjectIds: Option[];
  terminationId: Option | undefined;
  yearsOfExperience: number | null;

  primaryInstitutionId: Option | undefined;
  institutionIds: Option[];
  associatedInstitutions: PeopleProfileInstitutionFormType[];
};

type Props = {
  formikProps: FormikProps<FormType>;
  userRoles?: any;
};

const POST_LEVEL_OPTIONS: OptionNumber[] = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
];

const FieldsForSchoolOfficial: React.FC<Props> = props => {
  const { formikProps, userRoles } = props;
  const {
    values: { dateStarted, dateExited },
  } = formikProps;

  const canLinkInstitutions = ['INSTITUTION_ADMIN', 'INSTITUTION_MEMBER'].some(
    (item: any) => userRoles && userRoles.includes(item)
  );

  return (
    <React.Fragment>
      <FieldRow flex={0.5}>
        <Field
          name="emailAddress"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Email Address" mr={4} />
          )}
        />
        <WrappedQuery
          query={FILTERED_PERSONNEL_POSITIONS}
          variables={{
            input: {
              typeId: formikProps.values.typeId && formikProps.values.typeId.value,
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="personnelPositionId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Personnel Position"
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.filteredPersonnelPositions &&
                          data.filteredPersonnelPositions.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    mr={2}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
        {formikProps.values &&
          formikProps.values.personnelPositionId &&
          formikProps.values.personnelPositionId.label === 'Other' && (
            <Field
              name="personnelPositionOther"
              render={({ field }: FieldProps<FormType>) => (
                <StyledFormGroup {...field} field={field} form={formikProps} label="Personnel Position Other" mr={4} />
              )}
            />
          )}
      </FieldRow>
      <FieldRow>
        <Field
          name="postLevel"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormSelectFormik
              {...field}
              field={field}
              label="Post Level"
              form={formikProps}
              options={POST_LEVEL_OPTIONS}
              mr={4}
            />
          )}
        />
        <Field
          render={() => (
            <StyledFormikDatePicker
              isSingleDate={true}
              form={formikProps}
              label="Date Started"
              dateLabel="dateStarted"
              date={dateStarted || null}
              mr={4}
            />
          )}
        />
        <WrappedQuery query={ALL_NATURE_OF_EMPLOYMENTS} overrideStates={true}>
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="natureOfEmploymentId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Nature of Employment"
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.natureOfEmployment.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    mr={4}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
        <WrappedQuery query={ALL_EDUCATOR_LEVELS} overrideStates={true}>
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="educatorLevelId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Educator Level"
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.educatorLevels.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
      </FieldRow>
      <FieldRow>
        <Flex direction="row" size="0.25">
          <Field
            name="yearsOfExperience"
            render={({ field }: FieldProps<FormType>) => (
              <StyledFormGroup
                {...field}
                field={field}
                form={formikProps}
                label="Years of Experience"
                type="number"
                mr={4}
              />
            )}
          />
        </Flex>
        <Flex direction="row" size="0.25">
          <Field
            name="persalNumber"
            render={({ field }: FieldProps<FormType>) => (
              <StyledFormGroup {...field} field={field} form={formikProps} label="Persal Number" />
            )}
          />
        </Flex>
      </FieldRow>
      <Field
        name="leadEducator"
        render={({ field }: FieldProps<FormType>) => (
          <StyledFormGroup
            {...field}
            field={field}
            form={formikProps}
            label="Lead Educator"
            type="checkbox"
            mr={1}
            defaultChecked={formikProps.values.leadEducator}
          />
        )}
      />
      <FieldRow>
        <FieldRow flex={0.75}>
          <WrappedQuery query={ALL_ICT_SKILLS} overrideStates={true}>
            {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
              return (
                <Field
                  name="ictSkillId"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="ICT Skill"
                      form={formikProps}
                      options={
                        loading || error
                          ? []
                          : data.ictSkills.map((item: any) =>
                              formatSelectValue({
                                label: item.name,
                                value: item.id,
                              })
                            )
                      }
                      mr={3}
                    />
                  )}
                />
              );
            }}
          </WrappedQuery>
          <WrappedQuery query={ALL_HIGHEST_QUALIFICATIONS} overrideStates={true}>
            {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
              return (
                <Field
                  name="highestQualificationId"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Highest Qualification"
                      form={formikProps}
                      options={
                        loading || error
                          ? []
                          : data.highestQualifications.map((item: any) =>
                              formatSelectValue({
                                label: item.name,
                                value: item.id,
                              })
                            )
                      }
                      mr={3}
                    />
                  )}
                />
              );
            }}
          </WrappedQuery>
          <WrappedQuery query={ALL_RENUMERATION_SOURCES} overrideStates={true}>
            {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
              return (
                <Field
                  name="renumerationSourceId"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Remuneration Source"
                      form={formikProps}
                      options={
                        loading || error
                          ? []
                          : data.renumerationSources.map((item: any) =>
                              formatSelectValue({
                                label: item.name,
                                value: item.id,
                              })
                            )
                      }
                    />
                  )}
                />
              );
            }}
          </WrappedQuery>
        </FieldRow>
      </FieldRow>
      <Field
        name="multiGrade"
        render={({ field }: FieldProps<FormType>) => (
          <StyledFormGroup
            {...field}
            field={field}
            form={formikProps}
            label="Multi Grade"
            type="checkbox"
            mr={1}
            defaultChecked={formikProps.values.multiGrade}
          />
        )}
      />
      <FieldRow>
        <FieldRow flex={0.5}>
          <WrappedQuery query={ALL_GRADES} overrideStates={true}>
            {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
              return (
                <Field
                  name="gradeIds"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Grades"
                      form={formikProps}
                      isMulti={true}
                      options={
                        loading || error
                          ? []
                          : data.grades.map((item: any) =>
                              formatSelectValue({
                                label: item.value,
                                value: item.id,
                              })
                            )
                      }
                      mr={4}
                    />
                  )}
                />
              );
            }}
          </WrappedQuery>
        </FieldRow>
      </FieldRow>
      <FieldRow>
        <FieldRow flex={0.5}>
          <WrappedQuery
            query={FILTERED_SUBJECTS}
            variables={{
              input: {
                gradeIds:
                  formikProps.values.gradeIds &&
                  formikProps.values.gradeIds.map((grade: any) => {
                    return grade.value;
                  }),
              },
            }}
            overrideStates={true}
          >
            {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
              return (
                <Field
                  name="subjectIds"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Subjects"
                      form={formikProps}
                      isMulti={true}
                      options={
                        loading || error
                          ? []
                          : data.filteredSubjects &&
                            data.filteredSubjects.map((item: any) =>
                              formatSelectValue({
                                label: item.subjectGradeReference,
                                value: item.id,
                              })
                            )
                      }
                      mr={4}
                    />
                  )}
                />
              );
            }}
          </WrappedQuery>
        </FieldRow>
      </FieldRow>
      <FieldRow>
        <FieldRow flex={0.5}>
          <Field
            render={() => (
              <StyledFormikDatePicker
                isSingleDate={true}
                form={formikProps}
                label="Date Exited / Termination"
                dateLabel="dateExited"
                date={dateExited || null}
                mr={4}
              />
            )}
          />
          <WrappedQuery
            query={FILTERED_REASON_FOR_TERMINATIONS}
            variables={{
              input: {
                typeId: formikProps.values.typeId && formikProps.values.typeId.value,
              },
            }}
            overrideStates={true}
          >
            {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
              return (
                <Field
                  name="terminationId"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Reason for Exit"
                      form={formikProps}
                      options={
                        loading || error
                          ? []
                          : data.filteredReasonForTerminations &&
                            data.filteredReasonForTerminations.map((item: any) =>
                              formatSelectValue({
                                label: item.name,
                                value: item.id,
                              })
                            )
                      }
                      mr={2}
                    />
                  )}
                />
              );
            }}
          </WrappedQuery>
        </FieldRow>
      </FieldRow>
      <InstitutionsCard
        formikProps={formikProps}
        viewMode={formikProps.values.portal !== 'admin' && !canLinkInstitutions}
        currentInstitutionsOnly={formikProps.values.portal !== 'admin' && !canLinkInstitutions}
      />
    </React.Fragment>
  );
};

export default FieldsForSchoolOfficial;
